import LoadingSpinner from "@/components/common/LoadingSpinner";
import { CustomChildTable } from "@/components/CustomChildTable";
import { CustomImageUpload } from "@/components/CustomImageUpload";
import { CustomFileUpload } from "@/components/CustomFileUpload";
import { get } from "@/services/axiosUitils";
import {
  Checkbox,
  DatePicker as FormilyDatePicker,
  FormGrid,
  FormItem,
  FormLayout,
  FormTab,
  Input,
  NumberPicker,
  Password,
  Radio,
  Select,
  Switch,
  TimePicker,
  TreeSelect,
} from "@formily/antd-v5";
import { createSchemaField, FormProvider } from "@formily/react";
import {
  Card,
  Cascader,
  InputNumber,
  Rate,
  Slider,
  Tabs,
  Transfer,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { translateSchemaDeep } from "@/lib/translateSchemaDeep";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import { CustomMultipleLinksInput } from "@/components/CustomMultipleLinksInput";

const Text = ({ value, content, mode = "span", style = {}, ...props }: any) => {
  const Tag = mode ?? "span";
  return (
    <Tag style={style} {...props}>
      {content ?? value}
    </Tag>
  );
};

// Custom DatePicker wrapper with dd-mm-yyyy format
const DatePicker = (props: any) => {
  return (
    <FormilyDatePicker
      {...props}
      format={props.format ?? "DD-MM-YYYY"}
      valueFormat={props.valueFormat ?? "YYYY-MM-DD"}
    />
  );
};

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    NumberPicker,
    Password,
    Select,
    Checkbox,
    DatePicker,
    Switch,
    Radio,
    CustomFileUpload,
    GoogleAddressInput,
    CustomImageUpload,
    CustomMultipleLinksInput,
    FormLayout,
    Card,
    FormGrid,
    FormTab,
    Tabs,
    InputNumber,
    TreeSelect,
    TimePicker,
    Cascader,
    Transfer,
    Slider,
    Rate,
    TextArea,
    CustomChildTable,
    Text,
    ReactQuill,
  },
});

const SchemaFormRender = ({ schemaJson, form }: any) => {
  const { t } = useTranslation();

  // Move hooks before any early returns
  const [translatedSchema, setTranslatedSchema] = useState(() => {
    if (!schemaJson) {
      return null;
    }
    const schema = schemaJson.schema ?? schemaJson;
    return translateSchemaDeep(schema, t, "common");
  });

  const useAsyncDataSource =
    (service: (arg0: any) => Promise<any>, doc: any) => (field: any) => {
      field.loading = true;
      service(doc).then((data: any) => {
        field.dataSource = data;
        field.loading = false;
      });
    };

  const getOptions = async (schemaName: string) => {
    const data = await getOptionsApiCall(schemaName);
    return data;
  };

  async function getOptionsApiCall(schemaName: string) {
    return await get(`/dropdown/${schemaName}/`);
  }

  // Ensure all fields have a 'name' property set to their key
  // This is critical for fields like m0jxt9qkjqb that don't have explicit names
  // Also ensure DatePicker fields have dd-mm-yyyy format
  const ensureFieldNames = useCallback((obj: any, _parentKey?: string): any => {
    if (!obj || typeof obj !== "object") {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => ensureFieldNames(item));
    }

    const result: any = { ...obj };

    // If this object has properties, ensure each field has a name
    if (result.properties && typeof result.properties === "object") {
      result.properties = {};
      Object.keys(obj.properties).forEach(key => {
        const field = obj.properties[key];
        const processedField = ensureFieldNames(field, key);

        // Check if this is an input field (has x-component but is not a layout component)
        const isInputField =
          processedField["x-component"] &&
          typeof processedField["x-component"] === "string" &&
          !processedField["x-component"].includes("Form") &&
          processedField["x-component"] !== "Card" &&
          processedField["x-component"] !== "FormLayout" &&
          processedField["x-component"] !== "FormGrid" &&
          processedField["x-component"] !== "FormTab" &&
          processedField["x-component"] !== "Tabs";

        // If field has x-component (it's an input field) and no name, set it to the key
        if (isInputField && !processedField.name) {
          processedField.name = key;
          console.log(
            `[SchemaFormRender] Set field name for ${key} to "${key}"`
          );
        }

        // If this is a DatePicker field, ensure it has dd-mm-yyyy format
        if (processedField["x-component"] === "DatePicker") {
          processedField["x-component-props"] ??= {};
          // Set display format to dd-mm-yyyy
          processedField["x-component-props"].format ??= "DD-MM-YYYY";
          // Keep value format as YYYY-MM-DD for API compatibility
          processedField["x-component-props"].valueFormat ??= "YYYY-MM-DD";
        }

        result.properties[key] = processedField;
      });
    }

    return result;
  }, []);

  useEffect(() => {
    if (schemaJson) {
      const schema = schemaJson.schema ?? schemaJson;
      const translated = translateSchemaDeep(schema, t, "common");
      const withNames = ensureFieldNames(translated);
      setTranslatedSchema(withNames);
    }
  }, [schemaJson, t, ensureFieldNames]);

  if (!schemaJson) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const layoutProps = schemaJson.form ?? {};

  return (
    <FormProvider form={form}>
      <FormLayout {...layoutProps}>
        <SchemaField
          schema={translatedSchema}
          scope={{
            useAsyncDataSource,
            getOptions,
            t,
          }}
        />
      </FormLayout>
    </FormProvider>
  );
};

export default SchemaFormRender;
